<script setup>
import { computed, inject, ref, watch, onMounted } from 'vue'
import { useRoute, useRouter } from 'vue-router'
import Papa from 'papaparse'
import { RequestService } from '../shared/services/RequestService'
import { AccessCatalogService } from '../shared/services/AccessCatalogService'
import AccessCatalogKeysGuide from '../shared/components/PlatformAdmin/AccessCatalogKeysGuide.vue'
import BulkUserAccessUpload from '../shared/components/PlatformAdmin/BulkUserAccessUpload.vue'
import BcsTooltip from '../shared/components/BcsTooltip.vue'
import {
  parseCommaSeparatedKeys,
  catalogKeySet,
  findUnknownKeys,
  unknownKeysMessage,
  downloadCsv
} from '../shared/utils/accessCsv'

const route = useRoute()
const router = useRouter()
const csvFile = ref(null)
const csvInput = ref(null)
const loading = ref(false)
const processedUsers = ref([])
const errors = ref([])
const functionsBaseUrl = inject('functionsBaseUrl')
const catalog = ref({ roles: [], permissions: [] })
const catalogRoleKeys = ref(new Set())
const catalogPermissionKeys = ref(new Set())
const activeTab = ref('create')

const { post, handleErrors } = RequestService()
const accessCatalogService = AccessCatalogService()

function validateCatalogKeys(roleKeys, permissionGrants, permissionDenials) {
  const errs = []
  const roleMsg = unknownKeysMessage(findUnknownKeys(roleKeys, catalogRoleKeys.value), 'role key(s)')
  const grantMsg = unknownKeysMessage(findUnknownKeys(permissionGrants, catalogPermissionKeys.value), 'permission grant(s)')
  const denialMsg = unknownKeysMessage(findUnknownKeys(permissionDenials, catalogPermissionKeys.value), 'permission denial(s)')
  if (roleMsg) errs.push(roleMsg)
  if (grantMsg) errs.push(grantMsg)
  if (denialMsg) errs.push(denialMsg)
  return errs
}

function syncTabFromRoute() {
  activeTab.value = route.query.tab === 'update' ? 'update' : 'create'
}

function setTab(tab) {
  activeTab.value = tab
  router.replace({
    name: 'createUsers',
    query: tab === 'update' ? { tab: 'update' } : {}
  })
}

watch(() => route.query.tab, syncTabFromRoute, { immediate: true })

function csvColumnListHtml(columns) {
  const items = columns.map(({ name, type }) => (
    `<li><code>${name}</code> — ${type}</li>`
  )).join('')
  return [
    '<strong>CSV columns</strong>',
    `<ul style="margin:0.4rem 0 0;padding-left:1.15rem">${items}</ul>`
  ].join('')
}

const csvSchemaTooltip = computed(() => {
  if (activeTab.value === 'update') {
    return csvColumnListHtml([
      { name: 'Email', type: 'email (required)' },
      { name: 'RoleKeys', type: 'comma-separated catalog role keys' },
      { name: 'PermissionGrants', type: 'comma-separated catalog permission keys' },
      { name: 'PermissionDenials', type: 'comma-separated catalog permission keys' }
    ])
  }
  return csvColumnListHtml([
    { name: 'First Name', type: 'text' },
    { name: 'Last Name', type: 'text' },
    { name: 'Email', type: 'email (required)' },
    { name: 'Role/Title', type: 'text (job title)' },
    { name: 'Agency', type: 'agency id (required)' },
    { name: 'Phone', type: 'text' },
    { name: 'RoleKeys', type: 'comma-separated catalog role keys' },
    { name: 'PermissionGrants', type: 'comma-separated catalog permission keys' },
    { name: 'PermissionDenials', type: 'comma-separated catalog permission keys' }
  ])
})

onMounted(async () => {
  try {
    const loaded = await accessCatalogService.getCatalog()
    catalog.value = loaded
    catalogRoleKeys.value = catalogKeySet(loaded.roles)
    catalogPermissionKeys.value = catalogKeySet(loaded.permissions)
  } catch (error) {
    handleErrors('CreateUsers.loadCatalog', error)
  }
})

const processCSV = file =>
  new Promise((resolve, reject) => {
    Papa.parse(file, {
      header: true,
      skipEmptyLines: true,
      complete: ({ data }) => {
          const validUsers = []
          const invalidUsers = []
          const counts = {}
          data.forEach(u => {
            const e = u.Email?.toLowerCase().trim()
            if (e) counts[e] = (counts[e] || 0) + 1
          })
          data.forEach(u => {
            if (!Object.values(u).some(v => v?.trim())) return
            const e = u.Email?.toLowerCase().trim()
            const agency = u.Agency?.trim() || ''
            const firstName = u['First Name']?.trim() || ''
            const lastName = u['Last Name']?.trim() || ''
            const roleKeys = parseCommaSeparatedKeys(u.RoleKeys)
            const permissionGrants = parseCommaSeparatedKeys(u.PermissionGrants)
            const permissionDenials = parseCommaSeparatedKeys(u.PermissionDenials)
            const errs = []
            if (!e) errs.push('Email is required')
            if (!agency) errs.push('Agency is required')
            if (!(firstName || lastName)) errs.push('Name required')
            if (e && counts[e] > 1) errs.push('Duplicate email')
            errs.push(...validateCatalogKeys(roleKeys, permissionGrants, permissionDenials))
            const normalizedUser = {
              ...u,
              Email: e || '',
              Agency: agency,
              'First Name': firstName,
              'Last Name': lastName,
              roleKeys,
              permissionGrants,
              permissionDenials
            }
            if (errs.length) invalidUsers.push({ ...normalizedUser, errors: errs.join(', ') })
            else validUsers.push(normalizedUser)
          })
          resolve({ validUsers, invalidUsers })
        },
      error: err => reject(err)
    })
  })

  const handleFileUpload = e => {
    const file = e.target.files[0]
    if (file) csvFile.value = file
  }

  const processUsers = async () => {
    if (!csvFile.value) {
      handleErrors('Error processing users: ', new Error('Please select a CSV file'))
      return
    }
    loading.value = true
    processedUsers.value = []
    errors.value = []

    try {
      const { validUsers, invalidUsers } = await processCSV(csvFile.value)

      invalidUsers.forEach(user => {
        errors.value.push({ 
          email: user.Email || 'no email',
          error: user.errors
        })
      })

      if (validUsers.length) {
        let results = []
        try {
          const resp = await post(
            'createUsers',
            `${functionsBaseUrl}/usersGateway/api/v1/users`,
            validUsers
          )
          results = resp.data.data
        } catch (e) {
          console.error('Error calling createUsers function:', e)
          results = validUsers.map(u => ({
            success: false,
            email: u.Email,
            error: e.message
          }))
        }
        results.forEach(r => {
          if (r.success) {
            processedUsers.value.push({ email: r.email, status: r.status })
          }
          else {
            errors.value.push({ email: r.email, error: r.error || r.message })
          }
        })
      }
    } catch (e) {
      handleErrors('Error processing users: ', e)
      errors.value.push({ email: 'System Error', error: e.message })
    } finally {
      loading.value = false
      csvFile.value = null
      if (csvInput.value) csvInput.value.value = ''
    }
  }

  const downloadTemplate = () => {
    downloadCsv('user_template.csv', [
      'First Name,Last Name,Email,Role/Title,Agency,Phone,RoleKeys,PermissionGrants,PermissionDenials',
      'TestName,TestLastName,test123@test.com,A test role,1,111111,,,'
    ])
  }
</script>

<template>
  <div class="main-container">
    <div class="row d-flex justify-content-center">
      <div class="col-12 text-center my-3">
        <h1 class="mt-1 text-primary">Create Users</h1>
      </div>
    </div>

    <div class="row d-flex justify-content-center">
      <div class="col-md-10 col-lg-8">
        <ul class="nav nav-tabs mb-0">
          <li class="nav-item">
            <button
              class="nav-link"
              :class="{ active: activeTab === 'create' }"
              type="button"
              @click="setTab('create')"
            >
              Create users
            </button>
          </li>
          <li class="nav-item">
            <button
              class="nav-link"
              :class="{ active: activeTab === 'update' }"
              type="button"
              @click="setTab('update')"
            >
              Update user access
            </button>
          </li>
        </ul>

        <div class="card border-top-0 rounded-top-0">
          <div class="card-header bg-light">
            <h5 class="card-title header-20 text-secondary mb-0 d-flex align-items-center gap-2">
              <strong>{{ activeTab === 'create' ? 'Bulk User Upload' : 'Bulk Access Update' }}</strong>
              <BcsTooltip
                :simpleText="csvSchemaTooltip"
                :show-arrow="true"
                :contentClass="'simple-text-tooltip'"
                placement="bottom"
              >
                <i
                  class="bi bi-info-circle text-primary csv-schema-help"
                  role="img"
                  aria-label="CSV schema"
                />
              </BcsTooltip>
            </h5>
          </div>
          <div class="card-body">
            <template v-if="activeTab === 'create'">
            <div class="mb-4">
              <p>
                Upload a CSV file with the following columns:
                <strong>First Name, Last Name, Email, Role/Title, Agency, RoleKeys, PermissionGrants, PermissionDenials</strong>
              </p>
              <p class="text-muted">
                RoleKeys, PermissionGrants, and PermissionDenials are optional (comma-separated catalog keys).
                Invalid keys are rejected per row before upload. The first row should contain the column names.
              </p>
            </div>

            <AccessCatalogKeysGuide
              :roles="catalog.roles"
              :permissions="catalog.permissions"
            />

            <div class="mb-2">
              <button class="btn btn-outline-secondary" @click="downloadTemplate">Download CSV Template</button>
            </div>
            <div class="mb-4">
              <input
                ref="csvInput"
                type="file"
                class="form-control"
                id="csvFile"
                accept=".csv"
                @change="handleFileUpload"
                :disabled="loading" />
            </div>
            <button
              class="btn btn-primary"
              @click="processUsers"
              :disabled="!csvFile || loading">
              <span
                v-if="loading"
                class="spinner-border spinner-border-sm me-2"
                role="status"></span>
              {{ loading ? 'Processing...' : 'Process Users' }}
            </button>

            <div v-if="processedUsers.length > 0" class="mt-3">
              <h6>Successfully Processed Users:</h6>
              <ul class="list-group">
                <li
                  v-for="user in processedUsers"
                  :key="user.email"
                  class="list-group-item list-group-item-success">
                  {{ user.email }} - {{ user.status }}
                </li>
              </ul>
            </div>

            <div v-if="errors.length > 0" class="mt-3">
              <h6>Errors:</h6>
              <ul class="list-group">
                <li
                  v-for="error in errors"
                  :key="error.email"
                  class="list-group-item list-group-item-danger">
                  {{ error.email }} - {{ error.error }}
                </li>
              </ul>
            </div>
            </template>

            <BulkUserAccessUpload v-else :catalog="catalog" />
          </div>
        </div>
      </div>
    </div>
  </div>
</template>

<style scoped>
  .main-container {
    background-color: #f5f5f5;
    min-height: 100vh;
    padding-bottom: 3rem;
  }

  .csv-schema-help {
    font-size: 1rem;
    cursor: help;
  }
</style>
